import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { albumFormSchema, type AlbumFormData } from '@/schemas/albumSchemas';
import type { Album } from '@/types/legacy';
import { Loader2 } from 'lucide-react';

interface AlbumFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  album?: Album | null;
  onSuccess?: () => void;
}

export function AlbumFormDialog({ open, onOpenChange, album, onSuccess }: AlbumFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const form = useForm<AlbumFormData>({
    resolver: zodResolver(albumFormSchema),
    values: {
      name: album?.name || '',
      description: album?.description || '',
      is_active: album?.is_active ?? true,
    },
  });

  const onSubmit = async (data: AlbumFormData) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        description: data.description || null,
        is_active: data.is_active,
      };

      if (album) {
        const { error } = await api
          .from('albums')
          .update(payload)
          .eq('id', album.id);

        if (error) throw error;

        toast({
          title: 'Álbum actualizado',
          description: 'El álbum se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('albums')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Álbum creado',
          description: 'El álbum se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['albums'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el álbum',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{album ? 'Editar Álbum' : 'Nuevo Álbum'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Nombre del álbum" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del álbum (opcional)" rows={4} />
                  </FormControl>
                  <FormDescription>
                    Breve descripción del contenido del álbum
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El álbum es visible para todos los usuarios
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {album ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
